import React, { useState, useEffect, useRef } from 'react';
import { ActionHeader } from '../../makeNct';
import { camsRelations, kfinRelations } from '../../makeNct/UpdateNominee/addNominee';
import {
    NOMINEEINFO,
    initialValues,
    isMinorNominee,
} from '../../makeNct/UpdateNominee/update-nominee-edit';
import NomineeCard from '../../makeNct/UpdateNominee/NomineeCard';
import OptionalDetailsAccordion, {
    OptionalDetailsAccordionRef,
} from '../../makeNct/UpdateNominee/OptionalDetailsAccordion';
import {
    recalculateShares,
    handleShareChangeLogic,
    handleShareBlurLogic,
} from '../../makeNct/UpdateNominee/nomineeShareUtils';
import { getDate, emailRegex, mobileRegex, panRegex } from '../../../../utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../../../redux-store/reducers';
import { useHistory } from 'react-router';
import { UPDATE_PURCHASE_INFO } from '../../../../redux-store/types/newPurchase';
import { LoadProfileRoutes } from '.';
import enUs from '../../../../lang/en-us';
import { Button } from '@material-ui/core';

const { dashboard } = enUs;

function UpdatenomineePurchase(props: any) {
    const history = useHistory();
    const dispatch = useDispatch();
    const [nominees, setNominees] = useState<NOMINEEINFO[]>([]);
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
    const optionalDetailsRef = useRef<OptionalDetailsAccordionRef>(null);

    const {
        purchaseDetails = {},
        pan,
        investorName = '',
        displayName = '',
    }: any = useSelector(({ newPurchase, authReducer }: RootStateType) => ({
        ...newPurchase,
        ...authReducer,
    }));
    
    // Use displayName as fallback if investorName is empty
    const actualInvestorName = investorName || displayName;
    
    const { nominees: nomineeDetails = [], address } = purchaseDetails;
    const { kfinFlag } = props?.location?.state || {};
    const isKfin = !!kfinFlag;
    const relations = isKfin ? kfinRelations : camsRelations;

    useEffect(() => {
        if (!nomineeDetails?.length) {
            setNominees([
                {
                    ...initialValues,
                    share: 100,
                    isShareTouched: false,
                    isEditable: true,
                    isNewlyAdded: true,
                    isEdited: false,
                },
            ]);
            return;
        }
        const mappedNominees = nomineeDetails.map((_ele: any) => {
            if (!_ele) return null;
            const percentValue = _ele.percentage || _ele.share || _ele.percent;
            const hasValidPercent = percentValue !== '' && percentValue !== null && percentValue !== undefined;
            const nomineeState = {
                ..._ele,
                relation: relations.includes(_ele?.relation)
                    ? _ele.relation
                    : dashboard.UpdateNominee.selectRelation,
                name: _ele.name || '',
                addressSameAsFH: _ele.addressSameAsFH || 'Y',
                address1: address?.line1 || '',
                address2: address?.line2 || '',
                pincode: address?.pincode || '',
                city: address?.city || '',
                state: address?.state || '',
                country: address?.country || '',
                docName: _ele?.docName || '',
                docValue: _ele?.docValue || '',
                email: _ele?.email || '',
                mobile: _ele?.mobile || '',
                dob: getDate(_ele.dob),
                share: hasValidPercent ? percentValue : null,
                isShareTouched: false,
                pan: _ele.pan || '',
                gender: (_ele.gender || '').toLowerCase(),
                guardianName: _ele.nomineeGuardian?.name || _ele.guardianName || '',
                guardianPan: _ele.nomineeGuardian?.pan || _ele.guardianPan || '',
                guardianAddress: _ele.nomineeGuardian?.address || _ele.guardianAddress || '',
                guardianDob: _ele.nomineeGuardian?.dob || _ele.guardianDob || '',
                guardianRelation: _ele.nomineeGuardian?.relation || _ele.guardianRelation || '',
                isEditable: false,
                showDetails: false,
                editShare: false,
                isNewlyAdded: false,
                id: _ele.id,
                isEdited: false,
                isMinor: isMinorNominee(getDate(_ele.dob)) || _ele.isMinor || false,
            };
            return { ...nomineeState, initialState: nomineeState };
        }).filter(Boolean);
        let hasAnyEmptyShare = false;
        for (let i = 0; i < mappedNominees.length; i++) {
            if (mappedNominees[i] && mappedNominees[i].share === null) {
                hasAnyEmptyShare = true;
                break;
            }
        }
        if (hasAnyEmptyShare) {
            const nomineesWithShares = recalculateShares(mappedNominees);
            setNominees(nomineesWithShares);
        } else {
            setNominees(mappedNominees);
        }
    }, []);

    const handleNomineeChange = (key: any, value: any, index: number) => {
        if (key === 'share') {
            setNominees((prevNominees) => {
                return handleShareChangeLogic(String(value), index, prevNominees);
            });
        } else {
            setNominees((prevNominees) => {
                const updatedNominees = [...prevNominees];
                updatedNominees[index] = {
                    ...updatedNominees[index],
                    [key]: value,
                    isEdited: true,
                };
                return updatedNominees;
            });
        }
        if (fieldErrors[`${index}-${key}`]) {
            setFieldErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[`${index}-${key}`];
                return newErrors;
            });
        }
    };

    const handleShareBlur = (index: number) => {
        setNominees((prevNominees) => {
            const result = handleShareBlurLogic(index, prevNominees);
            return result !== null ? result : prevNominees;
        });
    };

    const handleAddNominee = () => {
        if (nominees.length >= 3) return;
        const newNominee: NOMINEEINFO = {
            ...initialValues,
            share: null as any,
            isShareTouched: false,
            isEditable: true,
            isNewlyAdded: true,
            isEdited: true,
        };
        const allNominees = [...nominees, newNominee];
        const updatedNominees = recalculateShares(allNominees);
        setNominees(updatedNominees.map((nom) => ({ ...nom, isEdited: true })));
    };

    const handleRemoveNominee = (index: number) => {
        const remainingNominees = nominees.filter((_, i) => i !== index);
        const updatedNominees = recalculateShares(remainingNominees);
        setNominees(updatedNominees.map((nom) => ({ ...nom, isEdited: true })));
    };

    const accordionFields = [
        'email',
        'mobile',
        'pan',
        'address1',
        'pincode',
        'docName',
        'docValue',
        'guardianName',
        'guardianPan',
        'guardianDob',
    ];

    const smoothScrollToElement = (element: Element | null) => {
        if (!element) return;
        
        try {
            const elementRect = element.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            const middle = absoluteElementTop - window.innerHeight / 2 + elementRect.height / 2;

            if ('scrollBehavior' in document.documentElement.style) {
                window.scrollTo({
                    top: middle,
                    behavior: 'smooth',
                });
            } else {
                const startPosition = window.pageYOffset;
                const distance = middle - startPosition;
                const duration = 500;
                let startTime: number | null = null;

                const animation = (currentTime: number) => {
                    if (startTime === null) startTime = currentTime;
                    const timeElapsed = currentTime - startTime;
                    const progress = Math.min(timeElapsed / duration, 1);
                    const ease =
                        progress < 0.5
                            ? 2 * progress * progress
                            : 1 - Math.pow(-2 * progress + 2, 2) / 2;
                    window.scrollTo(0, startPosition + distance * ease);
                    if (timeElapsed < duration) {
                        requestAnimationFrame(animation);
                    }
                };
                requestAnimationFrame(animation);
            }
        } catch (e) {
            // Silently fail if scroll fails
        }
    };

    const scrollToError = (nomineeIndex: number, fieldName: string) => {
        if (nomineeIndex === null || nomineeIndex === undefined || nomineeIndex < 0) return;
        if (!fieldName) return;

        const isAccordionField = accordionFields.includes(fieldName);

        if (isAccordionField) {
            optionalDetailsRef.current?.scrollToNomineeField(nomineeIndex, fieldName);
        } else {
            try {
                const fieldElement = document.querySelector(
                    `[data-nominee-index="${nomineeIndex}"][data-field-name="${fieldName}"]`,
                );
                if (fieldElement) {
                    smoothScrollToElement(fieldElement);
                    setTimeout(() => {
                        const inputElement = fieldElement.querySelector('input, select');
                        if (inputElement) {
                            (inputElement as HTMLElement).focus();
                        }
                    }, 550);
                }
            } catch (e) {
                // Silently fail if DOM element not found
            }
        }
    };

    const handleSave = () => {
        const errors: { [key: string]: string } = {};
        let firstErrorField: { index: number; field: string } | null = null;

        const drivingLicenseRegex = /^(([A-Z]{2}[0-9]{2})( )|([A-Z]{2}-[0-9]{2})|([A-Z]{2}[0-9]{2})())((19|20|[0-9]{2})[0-9][0-9])([0-9]{7}|[0-9]{8})$/;
        const aadharRegex = /^\d{4}$/;
        const passportNumberRegex = /^[A-Z]{1}[0-9]{7}$/;

        for (let i = 0; i < nominees.length; i++) {
            const nominee = nominees[i];
            if (!nominee.name || nominee.name.trim() === '') {
                errors[`${i}-name`] = `Please enter name for nominee ${i + 1}`;
                if (!firstErrorField) firstErrorField = { index: i, field: 'name' };
            } else if (actualInvestorName && nominee.name.trim().toLowerCase() === actualInvestorName.trim().toLowerCase()) {
                errors[`${i}-name`] = `Nominee name can not be investor name`;
                if (!firstErrorField) firstErrorField = { index: i, field: 'name' };
            }
            if (!nominee.relation || nominee.relation === dashboard.UpdateNominee.selectRelation) {
                errors[`${i}-relation`] = `Please select relationship for nominee ${i + 1}`;
                if (!firstErrorField) firstErrorField = { index: i, field: 'relation' };
            }
            if (!nominee.share || Number(nominee.share) <= 0) {
                errors[`${i}-share`] = `Please enter share % for nominee ${i + 1}`;
                if (!firstErrorField) firstErrorField = { index: i, field: 'share' };
            }
            if (nominee.isMinor && !nominee.dob) {
                errors[`${i}-dob`] = `Please enter date of birth for minor nominee ${i + 1}`;
                if (!firstErrorField) firstErrorField = { index: i, field: 'dob' };
            }

            if (nominee.email && nominee.email.trim() !== '') {
                if (!emailRegex.test(nominee.email)) {
                    errors[`${i}-email`] = `Please enter a valid email`;
                    if (!firstErrorField) firstErrorField = { index: i, field: 'email' };
                } else if (!nominee.isMinor) {
                    // Check for duplicate email across major nominees
                    for (let j = 0; j < i; j++) {
                        const otherEmail = nominees[j].email;
                        if (!nominees[j].isMinor && otherEmail && otherEmail.trim() !== '') {
                            if (nominee.email.trim().toLowerCase() === otherEmail.trim().toLowerCase()) {
                                errors[`${i}-email`] = `Nominee email should not be same as other nominee emails`;
                                if (!firstErrorField) firstErrorField = { index: i, field: 'email' };
                                break;
                            }
                        }
                    }
                }
            }

            if (nominee.mobile && nominee.mobile.trim() !== '') {
                if (!mobileRegex.test(nominee.mobile)) {
                    errors[`${i}-mobile`] = `Please enter a valid mobile number`;
                    if (!firstErrorField) firstErrorField = { index: i, field: 'mobile' };
                } else if (!nominee.isMinor) {
                    // Check for duplicate mobile across major nominees
                    for (let j = 0; j < i; j++) {
                        const otherMobile = nominees[j].mobile;
                        if (!nominees[j].isMinor && otherMobile && otherMobile.trim() !== '') {
                            if (nominee.mobile.trim() === otherMobile.trim()) {
                                errors[`${i}-mobile`] = `Nominee mobile should not be same as other nominee mobiles`;
                                if (!firstErrorField) firstErrorField = { index: i, field: 'mobile' };
                                break;
                            }
                        }
                    }
                }
            }

            if (nominee.pincode && String(nominee.pincode).trim() !== '') {
                const pincodeStr = String(nominee.pincode).trim();
                const isIndia =
                    !nominee.country || nominee.country === 'India' || nominee.country === '';
                if (isIndia) {
                    if (!/^\d{6}$/.test(pincodeStr)) {
                        errors[`${i}-pincode`] = `Please enter a valid 6-digit pincode`;
                        if (!firstErrorField) firstErrorField = { index: i, field: 'pincode' };
                    }
                } else {
                    if (!/^[A-Za-z0-9]{4,20}$/.test(pincodeStr)) {
                        errors[`${i}-pincode`] = `Please enter a valid pincode`;
                        if (!firstErrorField) firstErrorField = { index: i, field: 'pincode' };
                    }
                }
            }

            // If ID Number is entered but ID Type is not selected
            if (nominee.docValue && nominee.docValue.trim() !== '' && 
                (!nominee.docName || nominee.docName === dashboard.UpdateNominee.nomineeFields.docName)) {
                errors[`${i}-docName`] = `Please select ID Type`;
                if (!firstErrorField) firstErrorField = { index: i, field: 'docName' };
            }

            if (nominee.docName && nominee.docName !== dashboard.UpdateNominee.nomineeFields.docName) {
                if (!nominee.docValue || nominee.docValue.trim() === '') {
                    errors[`${i}-docValue`] = `Please enter ID Number`;
                    if (!firstErrorField) firstErrorField = { index: i, field: 'docValue' };
                } else {
                    const docValue = nominee.docValue.trim();
                    if (nominee.docName === 'A') {
                        if (!aadharRegex.test(docValue)) {
                            errors[`${i}-docValue`] = `Please enter last 4 digits of Aadhaar`;
                            if (!firstErrorField) firstErrorField = { index: i, field: 'docValue' };
                        } else if (docValue === '0000') {
                            errors[`${i}-docValue`] = `Please enter a valid Aadhaar number`;
                            if (!firstErrorField) firstErrorField = { index: i, field: 'docValue' };
                        } else {
                            // Check for duplicate Aadhaar across nominees
                            for (let j = 0; j < i; j++) {
                                const otherDocValue = nominees[j].docValue;
                                if (nominees[j].docName === 'A' && otherDocValue) {
                                    if (docValue === otherDocValue.trim()) {
                                        errors[`${i}-docValue`] = `Nominee Aadhaar should not be same as other nominee Aadhaar`;
                                        if (!firstErrorField) firstErrorField = { index: i, field: 'docValue' };
                                        break;
                                    }
                                }
                            }
                        }
                    } else if (nominee.docName === 'P') {
                        if (!panRegex.test(docValue)) {
                            errors[`${i}-docValue`] = `Please enter a valid PAN`;
                            if (!firstErrorField) firstErrorField = { index: i, field: 'docValue' };
                        } else if (pan && docValue.toUpperCase() === pan.toUpperCase()) {
                            errors[`${i}-docValue`] = `Nominee PAN should not be the investor PAN`;
                            if (!firstErrorField) firstErrorField = { index: i, field: 'docValue' };
                        } else {
                            // Check for duplicate PAN across nominees
                            for (let j = 0; j < i; j++) {
                                const otherDocValue = nominees[j].docValue;
                                if (nominees[j].docName === 'P' && otherDocValue) {
                                    if (docValue.toUpperCase() === otherDocValue.trim().toUpperCase()) {
                                        errors[`${i}-docValue`] = `Nominee PAN should not be same as other nominee PANs`;
                                        if (!firstErrorField) firstErrorField = { index: i, field: 'docValue' };
                                        break;
                                    }
                                }
                            }
                        }
                    } else if (nominee.docName === 'D') {
                        if (!drivingLicenseRegex.test(docValue)) {
                            errors[`${i}-docValue`] = `Please enter a valid driving license`;
                            if (!firstErrorField) firstErrorField = { index: i, field: 'docValue' };
                        } else {
                            // Check for duplicate Driving License across nominees
                            for (let j = 0; j < i; j++) {
                                const otherDocValue = nominees[j].docValue;
                                if (nominees[j].docName === 'D' && otherDocValue) {
                                    if (docValue.toUpperCase() === otherDocValue.trim().toUpperCase()) {
                                        errors[`${i}-docValue`] = `Nominee Driving License should not be same as other nominee Driving License`;
                                        if (!firstErrorField) firstErrorField = { index: i, field: 'docValue' };
                                        break;
                                    }
                                }
                            }
                        }
                    } else if (nominee.docName === 'I') {
                        if (!passportNumberRegex.test(docValue)) {
                            errors[`${i}-docValue`] = `Please enter a valid passport number`;
                            if (!firstErrorField) firstErrorField = { index: i, field: 'docValue' };
                        } else {
                            // Check for duplicate Passport across nominees
                            for (let j = 0; j < i; j++) {
                                const otherDocValue = nominees[j].docValue;
                                if (nominees[j].docName === 'I' && otherDocValue) {
                                    if (docValue.toUpperCase() === otherDocValue.trim().toUpperCase()) {
                                        errors[`${i}-docValue`] = `Nominee Passport should not be same as other nominee Passport`;
                                        if (!firstErrorField) firstErrorField = { index: i, field: 'docValue' };
                                        break;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if (nominee.isMinor) {
                if (!nominee.dob) {
                    errors[`${i}-dob`] = `Please enter date of birth for minor nominee`;
                    if (!firstErrorField) firstErrorField = { index: i, field: 'dob' };
                } else if (!isMinorNominee(nominee.dob)) {
                    errors[`${i}-dob`] = `DOB indicates nominee is not a minor`;
                    if (!firstErrorField) firstErrorField = { index: i, field: 'dob' };
                }

                // Guardian Name is optional, validate only if entered
                if (nominee.guardianName && nominee.guardianName.trim() !== '') {
                    if (nominee.guardianName.trim().length < 4) {
                        errors[`${i}-guardianName`] = `Invalid guardian name. Please enter a valid name`;
                        if (!firstErrorField) firstErrorField = { index: i, field: 'guardianName' };
                    } else if (nominee.name && nominee.guardianName.trim().toUpperCase() === nominee.name.trim().toUpperCase()) {
                        errors[`${i}-guardianName`] = `Guardian name should not be same as nominee name`;
                        if (!firstErrorField) firstErrorField = { index: i, field: 'guardianName' };
                    }
                }

                // Guardian PAN is optional, validate only if entered
                if (nominee.guardianPan && nominee.guardianPan.trim() !== '') {
                    if (!panRegex.test(nominee.guardianPan.trim())) {
                        errors[`${i}-guardianPan`] = `Please enter a valid guardian PAN`;
                        if (!firstErrorField) firstErrorField = { index: i, field: 'guardianPan' };
                    }
                }
            } else if (nominee.dob && isMinorNominee(nominee.dob)) {
                // DOB indicates minor but checkbox not selected
                errors[`${i}-isMinor`] = `Nominee ${i + 1}: DOB indicates nominee is below 18 years. Please select 'This nominee is below 18 years of age'`;
                if (!firstErrorField) firstErrorField = { index: i, field: 'isMinor' };
            }
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            if (firstErrorField) {
                scrollToError(firstErrorField.index, firstErrorField.field);
            }
            const firstKey = Object.keys(errors)[0];
            alert(errors[firstKey]);
            return;
        }

        // Check for duplicate nominee names (case-insensitive)
        if (nominees.length) {
            let names: string[] = [];
            let error = false;
            let duplicateIndex = 0;
            nominees.some((nominee, idx) => {
                const normalizedName = nominee.name.trim().toLowerCase();
                if (names.includes(normalizedName)) {
                    error = true;
                    duplicateIndex = idx;
                }
                names = [...names, normalizedName];
                return false;
            });
            if (error) {
                scrollToError(duplicateIndex, 'name');
                alert('Nominees name should be different from each other');
                return;
            }
        }

        const totalShare = nominees.reduce((sum, n) => sum + Number(n.share || 0), 0);
        if (totalShare !== 100) {
            scrollToError(0, 'share');
            alert(
                totalShare > 100
                    ? 'Total share percentage cannot exceed 100%'
                    : 'Total share percentage must equal 100%',
            );
            return;
        }

        const updatedNominees = nominees.map((nominee) => {
            const thisYear = new Date().getFullYear();
            const nomineeDobYear = nominee.dob
                ? new Date(nominee.dob).getFullYear()
                : thisYear;
            return {
                ...nominee,
                dob: nominee.isMinor ? nominee.dob : '',
                percentage: nominee.share,
                nomineeGuardian:
                    thisYear - nomineeDobYear < 18
                        ? {
                              name: nominee.guardianName || '',
                              pan: nominee.guardianPan || '',
                              dob: nominee.guardianDob || '',
                              relation: nominee.guardianRelation || '',
                          }
                        : { name: '', pan: '', dob: '', relation: '' },
            };
        });

        dispatch({
            type: UPDATE_PURCHASE_INFO,
            body: { nominees: updatedNominees },
        });
        history.goBack();
    };

    const handleCancel = () => {
        dispatch({
            type: UPDATE_PURCHASE_INFO,
            body: { nominees: nomineeDetails },
        });
        history.goBack();
    };

    return (
        <div className="curved-bg">
            <ActionHeader header={'Update Nominee Details'} />
            <div className="curved-bg-content curved-bg-table-outer update-nominee-screen">
                <div className="curved-bg-content-table">
                    <div className="curved-table-content update-nominee">
                        {/* Nominee Cards */}
                        {nominees.map((_ele: NOMINEEINFO, index: number) => (
                            <NomineeCard
                                key={_ele.id || index}
                                index={index}
                                nominee={_ele}
                                isKfin={isKfin}
                                totalNominees={nominees.length}
                                totalSharePercent={nominees.reduce(
                                    (sum, n) => sum + Number(n.share || 0),
                                    0,
                                )}
                                handleNomineeChange={(
                                    key: string,
                                    value: any,
                                    ind: number,
                                ) => {
                                    handleNomineeChange(key, value, ind);
                                }}
                                handleRemoveNominee={handleRemoveNominee}
                                onShareBlur={handleShareBlur}
                                errors={{
                                    name: fieldErrors[`${index}-name`] || '',
                                    relation: fieldErrors[`${index}-relation`] || '',
                                    share: fieldErrors[`${index}-share`] || '',
                                    dob: fieldErrors[`${index}-dob`] || '',
                                }}
                            />
                        ))}

                        {/* Add Nominee Button */}
                        {nominees.length < 3 && (
                            <div className="add-nominee-btn add-nominee-btn-dashed">
                                <a onClick={handleAddNominee}>
                                    + {dashboard.UpdateNominee.addNominee}
                                </a>
                            </div>
                        )}

                        {/* Optional Details Accordion */}
                        {nominees.length > 0 && (
                            <OptionalDetailsAccordion
                                ref={optionalDetailsRef}
                                nominees={nominees}
                                handleNomineeChange={(
                                    key: string,
                                    value: any,
                                    ind: number,
                                ) => {
                                    handleNomineeChange(key, value, ind);
                                }}
                                primaryHolderAddress={address}
                                fieldErrors={fieldErrors}
                            />
                        )}

                        {/* Action Buttons */}
                        <div
                            className="curved-bg-content-inner-bottom"
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '16px',
                                justifyContent: 'center',
                                marginTop: '24px',
                            }}
                        >
                            <Button
                                className="customBtn cancelBtn"
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="customBtn"
                                onClick={handleSave}
                            >
                                Save
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default (props: any) => LoadProfileRoutes(props, UpdatenomineePurchase);
